import React, { useEffect, useState, Fragment } from 'react';
import { Search, Bell, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
interface EmailMessage {
  from: string;
  date: string;
  body: string;
}
interface EmailThread {
  id: number;
  to: string;
  subject: string;
  amount: string;
  dateSent: string;
  reminderAt: string;
  status: 'accepted' | 'rejected' | 'awaiting';
  thread: EmailMessage[];
}
const emails: EmailThread[] = [{
  id: 1,
  to: 'ap_claims@dollargeneral.com',
  subject: 'Dispute - PO# 12345 - Shortage Claim',
  amount: '$500.00',
  dateSent: 'Feb 1, 2026',
  reminderAt: 'May 2, 2026',
  status: 'awaiting',
  thread: [{
    from: 'You',
    date: 'Feb 1, 2026',
    body: 'Hi team, this is regarding PO# 12345. We believe this shortage claim is incorrect — our BOL confirms 100 cases were shipped and signed for. Please see proof attached. We kindly request repayment of $500.00.'
  }]
}, {
  id: 2,
  to: 'vendor_support@walmart.com',
  subject: 'Dispute - PO# WM-9921 - Pricing Error',
  amount: '$1,250.00',
  dateSent: 'Feb 4, 2026',
  reminderAt: 'May 5, 2026',
  status: 'accepted',
  thread: [{
    from: 'You',
    date: 'Feb 4, 2026',
    body: 'Hi team, this is regarding PO# WM-9921. We believe this pricing deduction is incorrect — our invoice matches the agreed PO price. Please see proof attached. We kindly request repayment of $1,250.00.'
  }, {
    from: 'vendor_support@walmart.com',
    date: 'Feb 9, 2026',
    body: 'Accepted. We have reviewed your dispute and will process the repayment within 30 days.'
  }]
}, {
  id: 3,
  to: 'ap@target.com',
  subject: 'Dispute - PO# TGT-8812 - Shortage',
  amount: '$320.00',
  dateSent: 'Feb 7, 2026',
  reminderAt: 'May 8, 2026',
  status: 'rejected',
  thread: [{
    from: 'You',
    date: 'Feb 7, 2026',
    body: 'Hi team, this is regarding PO# TGT-8812. We believe this shortage claim is incorrect — our BOL confirms full delivery. Please see proof attached. We kindly request repayment of $320.00.'
  }, {
    from: 'ap@target.com',
    date: 'Feb 12, 2026',
    body: 'After review, we are unable to accept this dispute. Our receiving records confirm the shortage. The deduction stands.'
  }]
}, {
  id: 4,
  to: 'ap_claims@dollargeneral.com',
  subject: 'Dispute - PO# 12346 - Shortage Claim',
  amount: '$150.00',
  dateSent: 'Feb 11, 2026',
  reminderAt: 'May 12, 2026',
  status: 'awaiting',
  thread: [{
    from: 'You',
    date: 'Feb 11, 2026',
    body: 'Hi team, this is regarding PO# 12346. We believe this shortage claim is incorrect — our BOL confirms full shipment. Please see proof attached. We kindly request repayment of $150.00.'
  }]
}, {
  id: 5,
  to: 'ap_claims@dollargeneral.com',
  subject: 'Dispute - PO# 12347 - Shortage Claim',
  amount: '$800.00',
  dateSent: 'Feb 15, 2026',
  reminderAt: 'May 16, 2026',
  status: 'awaiting',
  thread: [{
    from: 'You',
    date: 'Feb 15, 2026',
    body: 'Hi team, this is regarding PO# 12347. We believe this shortage claim is incorrect — our BOL confirms full shipment. Please see proof attached. We kindly request repayment of $800.00.'
  }]
}, {
  id: 6,
  to: 'ap@wholefoodsmarket.com',
  subject: 'Dispute - PO# WF-4421 - Price Discrepancy',
  amount: '$640.00',
  dateSent: 'Feb 18, 2026',
  reminderAt: 'May 19, 2026',
  status: 'awaiting',
  thread: [{
    from: 'You',
    date: 'Feb 18, 2026',
    body: 'Hi team, this is regarding PO# WF-4421. We believe this price discrepancy deduction is incorrect — our invoice matches the agreed PO price. Please see proof attached. We kindly request repayment of $640.00.'
  }]
}, {
  id: 7,
  to: 'vendor@amazon.com',
  subject: 'Dispute - PO# AMZ-8801 - Short Quantity',
  amount: '$975.00',
  dateSent: 'Feb 20, 2026',
  reminderAt: 'May 21, 2026',
  status: 'awaiting',
  thread: [{
    from: 'You',
    date: 'Feb 20, 2026',
    body: 'Hi team, this is regarding PO# AMZ-8801. We believe this short quantity claim is incorrect — our carrier signed for full delivery. Please see proof attached. We kindly request repayment of $975.00.'
  }]
}];
const statusConfig = {
  accepted: {
    label: 'Accepted by Retailer',
    type: 'success' as const
  },
  rejected: {
    label: 'Rejected by Retailer',
    type: 'error' as const
  },
  awaiting: {
    label: 'Awaiting Reply',
    type: 'warning' as const
  }
};
export function Outbox() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set([2]));
  // Auto-expand an email if ?expand=<id> is in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const expandId = params.get('expand');
    if (expandId) {
      setExpanded(new Set([Number(expandId)]));
    }
  }, []);
  const filtered = emails.filter(e => {
    const q = query.toLowerCase();
    if (!q) return true;
    return e.to.toLowerCase().includes(q) || e.subject.toLowerCase().includes(q) || e.amount.toLowerCase().includes(q);
  });
  const allSelected = filtered.length > 0 && filtered.every(e => selected.has(e.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(e => next.delete(e.id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(e => next.add(e.id));
        return next;
      });
    }
  };
  const toggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const anySelected = selected.size > 0;
  return <div className="space-y-6" data-id="element-820">
      <div className="flex items-center justify-between" data-id="element-821">
        <div data-id="element-822">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-823">
            Mailbox
          </h2>
          <p className="text-slate-500 mt-1" data-id="element-824">
            View sent emails and schedule reminders. Note you cannot reply
            directly here — to do that, find the email in Outlook.
          </p>
        </div>
        <button disabled={!anySelected} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${anySelected ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-sm' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`} data-id="element-825">
          <Send className="w-4 h-4" data-id="element-826" />
          Send Reminder Now
          {anySelected && <span className="ml-1 bg-white/20 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-827">
              {selected.size}
            </span>}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-828">
        {/* Search toolbar */}
        <div className="p-4 border-b border-slate-100" data-id="element-829">
          <div className="relative max-w-sm" data-id="element-830">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-831">
              <Search className="h-4 w-4 text-slate-400" data-id="element-832" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg bg-slate-50 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition" placeholder="Search by recipient, subject, amount, or PO #…" value={query} onChange={e => setQuery(e.target.value)} data-id="element-833" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-b-xl" data-id="element-834">
          <table className="w-full text-sm text-left" data-id="element-835">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-836">
              <tr data-id="element-837">
                <th className="px-4 py-3 w-10" data-id="element-838">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-id="element-839" />
                </th>
                <th className="px-4 py-3" data-id="element-840">Recipient / Subject</th>
                <th className="px-4 py-3" data-id="element-841">Date Sent</th>
                <th className="px-4 py-3" data-id="element-842">Amount</th>
                <th className="px-4 py-3" data-id="element-843">Status</th>
                <th className="px-4 py-3" data-id="element-844">Send Reminder At</th>
                <th className="px-4 py-3 w-8" data-id="element-845"></th>
              </tr>
            </thead>
            <tbody data-id="element-846">
              {filtered.length > 0 ? filtered.map(email => {
              const isExpanded = expanded.has(email.id);
              const isSelected = selected.has(email.id);
              const {
                label,
                type
              } = statusConfig[email.status];
              return <Fragment key={email.id} data-id="element-847">
                      <tr onClick={() => toggleExpand(email.id)} className={`border-t border-slate-100 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`} data-id="element-848">
                        <td className="px-4 py-4" onClick={e => toggleSelect(email.id, e)} data-id="element-849">
                          <input type="checkbox" checked={isSelected} onChange={() => {}} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-id="element-850" />
                        </td>
                        <td className="px-4 py-4" data-id="element-851">
                          <div className="font-medium text-slate-900" data-id="element-852">
                            {email.to}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5" data-id="element-853">
                            {email.subject}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600 whitespace-nowrap" data-id="element-854">
                          {email.dateSent}
                        </td>
                        <td className="px-4 py-4 font-medium text-slate-900" data-id="element-855">
                          {email.amount}
                        </td>
                        <td className="px-4 py-4" data-id="element-856">
                          <StatusBadge status={type} data-id="element-857">{label}</StatusBadge>
                        </td>
                        <td className="px-4 py-4" data-id="element-858">
                          <div className="flex items-center gap-1.5 text-amber-600 text-xs font-medium" data-id="element-859">
                            <Bell className="w-3.5 h-3.5" data-id="element-860" />
                            {email.reminderAt}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-400" data-id="element-861">
                          {isExpanded ? <ChevronUp className="w-4 h-4" data-id="element-862" /> : <ChevronDown className="w-4 h-4" data-id="element-863" />}
                        </td>
                      </tr>

                      {/* Expanded thread */}
                      {isExpanded && <tr className="border-t border-slate-100 bg-slate-50" data-id="element-864">
                          <td colSpan={7} className="px-8 pb-5 pt-2" data-id="element-865">
                            <div className="space-y-3 max-w-2xl" data-id="element-866">
                              {email.thread.map((msg, i) => {
                        const isOutbound = msg.from === 'You';
                        return <div key={i} className={`rounded-lg border p-4 text-sm ${isOutbound ? 'bg-white border-slate-200' : email.status === 'accepted' ? 'bg-emerald-50 border-emerald-200' : email.status === 'rejected' ? 'bg-rose-50 border-rose-200' : 'bg-blue-50 border-blue-200'}`} data-id="element-867">
                                    <div className="flex items-center justify-between mb-2" data-id="element-868">
                                      <span className={`text-xs font-semibold ${isOutbound ? 'text-slate-600' : 'text-slate-700'}`} data-id="element-869">
                                        {isOutbound ? 'You' : msg.from}
                                      </span>
                                      <span className="text-xs text-slate-400" data-id="element-870">
                                        {msg.date}
                                      </span>
                                    </div>
                                    <p className="text-slate-700 leading-relaxed" data-id="element-871">
                                      {msg.body}
                                    </p>
                                  </div>;
                      })}
                            </div>
                          </td>
                        </tr>}
                    </Fragment>;
            }) : <tr data-id="element-872">
                  <td colSpan={7} className="px-6 py-8 text-center text-slate-400 text-sm" data-id="element-873">
                    No emails match your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}